import { checkHasMoreItem } from 'helpers';
import { showError } from 'helpers/handleError';
import { useEffect, useState, useCallback, useRef } from 'react';
import invoicesServices from 'services/InvoiceServices';
import cloneDeep from 'lodash/cloneDeep';
import { PayViaEnum } from 'consts/data';
import PromiseHandler from 'helpers/promiseHandler';
import { timezoneHelpers } from 'providers/TimezoneProvider';

const useGetListTransaction = (filters, isTrigger = true) => {
  const promiseHandler = useRef(new PromiseHandler());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetch = useCallback(
    ({ isLoadmore = false, offset } = {}) => {
      if (!isTrigger) {
        return;
      }

      return new Promise(async (resolve, reject) => {
        const { from: startDate, to: endDate } = timezoneHelpers.convertDateRangeByTimezone({
          from: filters?.dateRanges?.from,
          to: filters?.dateRanges?.to,
          shouldReturnTimestamp: true,
        });

        try {
          const parseBody = {
            id: filters?.id,
            type: filters.type,
            start: startDate || null,
            end: endDate || null,

            offset: offset ?? filters.offset,
            limit: filters.limit,

            //* Bank type
            statuses: (filters?.statuses || [])?.map((el) => el.value),
            // showDeletedFile: filters.showDeletedFile || false,

            //* Credit type
            afterId: filters.type === PayViaEnum.CreditCard ? filters?.afterId : undefined,
          };

          const response = await promiseHandler.current.takeLatest(
            invoicesServices.listTransaction(parseBody)
          );

          //! Change response here
          if (isLoadmore) {
            setData((prev) => {
              const nextPages = cloneDeep(prev);
              nextPages.result = [...(nextPages?.result || []), ...response?.data?.result];
              return nextPages;
            });
          } else {
            setData(response?.data);
          }

          setHasMore(checkHasMoreItem(response?.data?.result, filters.limit));

          resolve();
        } catch (error) {
          if (error.isCanceled) {
            return;
          }

          showError(error);
          setError(error);
          reject(error);
        }
      });
    },
    [filters, isTrigger]
  );

  const fetchMore = useCallback(async () => {
    setLoadingMore(true);
    await fetch({ isLoadmore: true });
    setLoadingMore(false);
  }, [fetch]);

  const refetch = useCallback(
    async (options) => {
      setRefetching(true);
      await fetch(options);
      setRefetching(false);
    },
    [fetch]
  );

  const refetchWithLoading = useCallback(async () => {
    try {
      setLoading(true);
      await fetch();
      setLoading(false);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
  }, [fetch]);

  useEffect(() => {
    if (!!filters.afterId) {
      fetchMore();
      return;
    }

    if (!filters.offset || filters.offset <= 0) {
      refetchWithLoading();
    } else {
      fetchMore();
    }
  }, [filters.afterId, filters.offset, fetchMore, refetchWithLoading]);

  return { data, loading, error, refetch, refetchWithLoading, refetching, loadingMore, hasMore };
};

export default useGetListTransaction;
